import {useEffect, useState} from "react";
import Modal from "../../ui-components/Modal";
import Input from "../../ui-components/Input";
import Button from "../../ui-components/Button";
import {FormattedMessage} from "react-intl";
import {ExclamationTriangleIcon} from "@heroicons/react/24/outline";
import { validateEmail } from "@utils/validate-email";
import {useCheckMailExistence} from "@feature/users/hooks/useCheckMailExistence";


const Warning = ({title, description}) => (
  <p className="bg-yellow-100 py-2 px-3 border-l-4 border-yellow-400 text-yellow-800">
    <h4 className="flex items-center gap-1.5 font-medium">
      <ExclamationTriangleIcon className="w-4 mt-0.5"/>
      {title}
    </h4>
    <p className="mt-1.5 text-xs">
      {description}
    </p>
  </p>
)


const EmailUserInvitationModal = ({
    intl,
    inviteeFullName,
    inviteeEmail=null,
    onClose=() => {},
    onConfirm=() => {},
}) => {

  // hooks
  const {check, loading} = useCheckMailExistence();

  // state
  const [email, setEmail] = useState(inviteeEmail || "");
  const [submitting, setSubmitting] = useState(false);
  const [warnings, setWarnings] = useState({
    alreadyInvited: !!inviteeEmail,
    existingInvitation: false,
    existingUser: false,
  });

  // utilities
  const isEmailValid = !!email && validateEmail(email);

  const warningsContentLookup = {
    alreadyInvited: {
      title: <FormattedMessage
        id="invitation_already_sent_warning_title"
        defaultMessage="Invito già mandato"
      />,
      description: <FormattedMessage
        id="invitation_already_sent_warning_description"
        defaultMessage={
          "In passato è stata inviata una mail di invito a questo utente (ultimo indirizzo utilizzato: {email}). " +
          "È possibile mandare un nuovo invito utilizzando lo stesso indirizzo email, oppure inserirne uno diverso"
        }
        values={{email: <span className="font-semibold">{inviteeEmail}</span>}}
      />,
    },
    existingInvitation: {
      title: <FormattedMessage
        id="conflicting_invitation_warning_title"
        defaultMessage="Mail già utilizzata"
      />,
      description: <FormattedMessage
        id="conflicting_invitation_warning_description"
        defaultMessage={
          "L'email inserita è stata già utilizzata per invitare un altro utente che non ha ancora completato il " +
          "processo di registrazione. Cliccando su INVITA l'invito esistente sovrascritto da questo, ed il relativo " +
          "link di registrazione non risulterà più valido"
        }
      />,
    },
  };
  const activeWarnings = Object.keys(warnings)
    .filter((k) => warnings[k] === true && !!warningsContentLookup[k]);

  useEffect(() => {
    // whenever the email changes, check if it exists
    if (!isEmailValid)
      return;
    check(email).then((res) => {
      setWarnings((prev) => ({
        ...prev,
        existingInvitation: inviteeEmail !== email && res.has_pending_invitation,
        existingUser: res.has_registered_user,
      }));
    });
  }, [email]);  // eslint-disable-line react-hooks/exhaustive-deps

  return <Modal onExit={onClose}>
    <h3 className="text-lg leading-6 text-center text-black-500">
      <FormattedMessage id="invite_user_on_GL" />
    </h3>
    <p className="mt-8">
      <FormattedMessage
        id="invite_user_on_GL_helper"
        values={{fullName: <span className="font-bold">{inviteeFullName}</span>}}
      />
    </p>
    {isEmailValid && activeWarnings.length > 0 && (
      <div className="mt-5 flex flex-col gap-3">
        {
          activeWarnings
            .filter((k) => warnings[k] === true)
            .map((k, idx) => {
              const values = warningsContentLookup[k];
              if (!values)
                return null;
              return <Warning key={idx} {...values} />
            })
        }
      </div>
    )}
    <div className="my-6">
      <Input
        type="email"
        label={intl.formatMessage({id: 'email'})}
        value={email}
        onChange={(evt) => { setEmail(evt.target.value); }}
        required={true}
        errorMessage={intl.formatMessage({id: 'required_field'})}
      />
    </div>
    <div className="flex flex-col sm:flex-row gap-3">
      <div className="flex-grow">
        <Button
          full
          styleType="empty"
          onClick={onClose}
        >
          <FormattedMessage id="cancel" />
        </Button>
      </div>
      <div className="flex-grow">
        <Button
          full
          onClick={async () => {
            setSubmitting(true);
            try {
              const isMerge = warnings.existingUser;
              await onConfirm(email, isMerge);
            }
            finally {
              setSubmitting(false);
            }
          }}
          disabled={!isEmailValid || loading}
          submitting={submitting}
        >
          <FormattedMessage id="invite" />
        </Button>
      </div>
    </div>
  </Modal>
}

export default EmailUserInvitationModal;